<template>
  <div>
    <template v-if="loading">
      <div class="tw-p-4"><WdsSpinner indeterminate :size="40" /></div>
    </template>

    <FreelancerBusinessEntityAlert
      v-if="freelancerBusinessEntityCompliance !== null"
      :market="freelancerBusinessEntityCompliance.data.market"
    />

    <template v-if="viewerAccountType === 'recruiter'">
      <RecruiterBusinessEntityAlert v-if="recruiterBusinessEntityCompliance !== null" />
    </template>

    <AcceptHireDeterminationAlert
      v-if="determinationCompliance !== null"
      :compliance-data="determinationCompliance.data"
      :hire="hire"
    />

    <template v-for="compliance in compliancesHire">
      <template v-if="viewerAccountType === 'company'">
        <UsWorkerExemptAlertCompany
          v-if="compliance.name === ComplianceName.FLSA"
          :key="compliance.name"
          :compliance-data="compliance.data"
        />

        <BackgroundChecksCompanyAlert
          v-if="compliance.name === ComplianceName.BACKGROUND_CHECKS"
          :key="compliance.name"
          :compliance-data="compliance.data"
        />
      </template>
      <template v-if="viewerAccountType === 'freelancer' || viewerAccountType === 'recruiter'">
        <AddressIncompleteAlert
          v-if="compliance.name === ComplianceName.ADDRESS"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <AcceptContractDocsAlert
          v-if="compliance.name === ComplianceName.CONTRACT_DOC"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <BackgroundChecksFreelancerAlert
          v-if="compliance.name === ComplianceName.BACKGROUND_CHECKS"
          :key="compliance.name"
          :compliance-data="compliance.data"
        />

        <FullNameIncompleteAlert
          v-if="compliance.name === ComplianceName.FULL_NAME"
          :key="compliance.name"
        />

        <GlobalContractTypeHireAlert
          v-if="compliance.name === ComplianceName.GLOBAL_CONTRACT_TYPE_HIRE"
          :key="compliance.name"
        />

        <OnsitePresenceRequiredAlert
          v-if="compliance.name === ComplianceName.ONSITE_PRESENCE"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <UkContractTypeMissing
          v-if="compliance.name === ComplianceName.UK_CONTRACT_TYPE_SOLE_TRADER"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <PayeEmploymentAlert
          v-if="compliance.name === ComplianceName.UK_PAYE_EMPLOYMENT"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <PayeContractDatesRequiredAlert
          v-if="compliance.name === ComplianceName.UK_PAYE_REQUIRED_CONTRACT_DATES"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <PayeContractPeriodOverlapping
          v-if="compliance.name === ComplianceName.UK_PAYROLL_CONTRACT_PERIOD"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <PayeSettingsRequiredAlert
          v-if="compliance.name === ComplianceName.UK_PAYROLL"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <UsTaxAlert
          v-if="compliance.name === ComplianceName.US_TAX"
          :key="compliance.name"
          :compliance-data="compliance.data"
        />

        <UsWorkerExemptAlertFreelancer
          v-if="compliance.name === ComplianceName.FLSA"
          :key="compliance.name"
          :compliance-data="compliance.data"
        />

        <UkValidContractSetupAlert
          v-if="compliance.name === ComplianceName.UK_VALID_CONTRACT_SETUP"
          :key="compliance.name"
          :compliance-data="compliance.data"
        />
      </template>
    </template>
  </div>
</template>

<script setup>
import { useQuery } from '@vue/apollo-composable'
import { WdsSpinner } from '@worksome/wds-components'
import gql from 'graphql-tag'
import { computed } from 'vue'
import RecruiterBusinessEntityAlert from '../BusinessEntity/alerts/RecruiterBusinessEntityAlert.vue'
import AcceptContractDocsAlert, {
  fragments as AcceptContractDocsAlertFragments,
} from '@/components/compliances/alerts/AcceptContractDocsAlert.vue'
import AddressIncompleteAlert from '@/components/compliances/alerts/AddressIncompleteAlert.vue'
import FreelancerBusinessEntityAlert from '@/components/compliances/BusinessEntity/alerts/FreelancerBusinessEntityAlert.vue'
import FullNameIncompleteAlert from '@/components/compliances/alerts/FullNameIncompleteAlert.vue'
import GlobalContractTypeHireAlert from '@/components/compliances/alerts/GlobalContractTypeHireAlert.vue'
import OnsitePresenceRequiredAlert from '@/components/compliances/alerts/OnsitePresenceRequiredAlert.vue'
import PayeContractDatesRequiredAlert from '@/components/compliances/alerts/PayeContractDatesRequiredAlert.vue'
import PayeContractPeriodOverlapping from '@/components/compliances/alerts/PayeContractPeriodOverlapping.vue'
import PayeEmploymentAlert from '@/components/compliances/alerts/PayeEmploymentAlert.vue'
import PayeSettingsRequiredAlert from '@/components/compliances/alerts/PayeSettingsRequiredAlert.vue'
import UkContractTypeMissing from '@/components/compliances/alerts/UkContractTypeMissing.vue'
import UsTaxAlert from '@/components/compliances/alerts/UsTaxAlert.vue'
import UsWorkerExemptAlertFreelancer from '@/components/compliances/alerts/UsWorkerExemptAlertFreelancer.vue'
import UsWorkerExemptAlertCompany from '@/components/compliances/alerts/UsWorkerExemptAlertCompany.vue'
import UkValidContractSetupAlert from '@/components/compliances/alerts/UkValidContractSetupAlert.vue'
import AcceptHireDeterminationAlert, {
  fragments as AcceptHireDeterminationAlertFragments,
} from '@/components/compliances/Determinations/alerts/AcceptHireDeterminationAlert.vue'
import {
  ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT,
  ACTIVE_COMPLIANCES_ON_HIRE_FRAGMENT,
} from '@/components/compliances/queries/Compliances'
import {
  ComplianceName,
  determinationCompliances,
  freelancerBusinessEntityCompliances,
  recruiterBusinessEntityCompliances,
} from '@/components/compliances/types/ComplianceName.enum'
import { ParseJSONPropertyInObject } from '@/utils/ParseJSONPropertyInObject'
import BackgroundChecksCompanyAlert from '@/components/compliances/alerts/BackgroundChecksCompanyAlert.vue'
import BackgroundChecksFreelancerAlert from '@/components/compliances/alerts/BackgroundChecksFreelancerAlert.vue'

const props = defineProps({
  hireId: {
    type: Number,
    required: true,
  },
})

const viewerAccountType = computed(() => currentAccountType)

const { result: resultHire, loading: loadingHire } = useQuery(
  gql`
    query getHire($hireId: Int!, $complianceNames: [ComplianceName!]!) {
      hire(localId: $hireId) {
        ...ActiveCompliancesOnHire
        ...AcceptContractDocsAlertHire
        ...AcceptHireDeterminationAlertHire
      }
    }
    ${ACTIVE_COMPLIANCES_ON_HIRE_FRAGMENT}
    ${AcceptContractDocsAlertFragments.hire}
    ${AcceptHireDeterminationAlertFragments.hire}
  `,
  {
    hireId: props.hireId,
    complianceNames: [
      ...determinationCompliances(),
      ComplianceName.ADDRESS,
      ComplianceName.BACKGROUND_CHECKS,
      ComplianceName.CONTRACT_DOC,
      ComplianceName.FULL_NAME,
      ComplianceName.GLOBAL_CONTRACT_TYPE_HIRE,
      ComplianceName.ONSITE_PRESENCE,
      ComplianceName.IDENTIFICATION,
      ComplianceName.UK_CONTRACT_TYPE_SOLE_TRADER,
      ComplianceName.UK_PAYE_EMPLOYMENT,
      ComplianceName.UK_PAYE_REQUIRED_CONTRACT_DATES,
      ComplianceName.UK_PAYROLL,
      ComplianceName.UK_PAYROLL_CONTRACT_PERIOD,
      ComplianceName.UK_VALID_CONTRACT_SETUP,
      ComplianceName.US_TAX,
      ComplianceName.FLSA,
    ],
  },
)
const hire = computed(() => resultHire?.value?.hire)
const compliancesHire = computed(
  () =>
    hire.value?.compliances
      ?.filter((compliance) => compliance.isApplicable && !compliance.isComplete)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)
const determinationCompliance = computed(
  () =>
    compliancesHire.value.find((compliance) =>
      determinationCompliances().includes(compliance.name),
    ) ?? null,
)

const { result: resultAccount, loading: loadingAccount } = useQuery(
  gql`
    query accountQuery($complianceNames: [ComplianceName!]!) {
      me {
        activeAccount {
          __typename
          ...ActiveCompliancesOnAccount
        }
      }
    }
    ${ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT}
  `,
  {
    complianceNames: [
      ...freelancerBusinessEntityCompliances(),
      ...recruiterBusinessEntityCompliances(),
    ],
  },
)
const activeAccount = computed(() => resultAccount?.value?.me?.activeAccount)
const compliancesAccount = computed(
  () =>
    activeAccount.value?.compliances
      ?.filter((compliance) => compliance.isApplicable && !compliance.isComplete)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)
const freelancerBusinessEntityCompliance = computed(
  () =>
    compliancesAccount.value.find((compliance) =>
      freelancerBusinessEntityCompliances().includes(compliance.name),
    ) ?? null,
)
const recruiterBusinessEntityCompliance = computed(
  () =>
    compliancesAccount.value.find((compliance) =>
      recruiterBusinessEntityCompliances().includes(compliance.name),
    ) ?? null,
)

const loading = computed(() => loadingHire.value || loadingAccount.value)
</script>
